<script setup>
import { deepCopy } from '@/utilities/index.util.js';
import vPopover from '@/components/elements/v-popover.vue';
import VGenericInput from '@/components/inputs/generic.input.vue';
import VFilterGenericInput from '@/components/inputs/filter-generic.input.vue';
import { onMounted, ref, computed, watch } from 'vue';
import { useEventListener } from '@/directive/use-event-listener';

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, default: 'Field' },
  value: { type: [Number, String], default: null },
  results: { type: Array, default: () => [] },
  placeholder: { type: String, default: null },
  loading: { type: Number, default: 100 },
  index: { type: Number, default: 0 },
  focus: { type: Boolean, default: false },
  dynamic: { type: Boolean, default: false },
  inline: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  typeToOpen: { type: Boolean, default: false }
});

const selectedIndex = ref(0);
const fieldRef = ref(null);
const inputDomRect = ref(null);
const localValue = ref(props.value);
const popoverRef = ref(null);

onMounted(() => {
  inputDomRect.value = fieldRef.value?.getBoundingClientRect();
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal !== localValue.value) {
      localValue.value = newVal;
    }
  }
);

const upDownKeyPress = (e) => {
  if (!props.results?.length) return;
  if (!popoverRef.value.visible) return;
  if (
    e.key === 'ArrowUp' ||
    e.key === 'ArrowDown' ||
    e.key === 'Enter' ||
    e.key === 'Tab' ||
    e.key === 'Escape'
  ) {
    e.preventDefault();
    let index = deepCopy(selectedIndex.value);
    switch (e.key) {
      case 'ArrowUp':
        index--;
        break;
      case 'ArrowDown':
        index++;
        break;
      case 'Enter':
        select(props.results[selectedIndex.value]);
        deactivate();
        break;
      case 'Tab':
      case 'Escape':
        deactivate();
        break;
    }
    index = index > props.results.length - 1 ? props.results.length - 1 : index;
    index = index < 0 ? 0 : index;
    selectedIndex.value = index;
  }
};

useEventListener(window, 'keydown', upDownKeyPress);
useEventListener(window, 'resize', () => {
  inputDomRect.value = fieldRef.value?.getBoundingClientRect();
});

const emit = defineEmits(['change', 'select', 'open', 'close']);

const activate = () => {
  selectedIndex.value = 0;
  popoverRef.value?.show();
  popoverRef.value.visible = true;
  emit('open');
};

const deactivate = () => {
  popoverRef.value.visible = false;
  popoverRef.value?.hide();
  document.activeElement.blur();
  emit('close');
};

const select = (val) => {
  const index = props.results.findIndex((result) => result.name === val.name);
  emit('select', { result: val, index });
  deactivate();
};
const change = (i) => {
  selectedIndex.value = i;
};
</script>

<template>
  <div
    class="v-type-ahead-input"
    ref="fieldRef"
    :class="[{ dynamic: dynamic }]"
  >
    <v-popover ref="popoverRef" state="manual" side="bottom" alignment="start">
      <template #trigger>
        <v-generic-input
          v-if="!inline"
          :model-value="localValue"
          :focus="focus"
          :name="name"
          :label="label"
          :placeholder="placeholder"
          @focus.stop="!typeToOpen ? activate() : () => undefined"
          @click.stop="!typeToOpen ? activate() : () => undefined"
          :debounce="500"
          autocomplete="off"
          :dynamic="dynamic"
          :disabled="disabled"
          @input="
            (e) => {
              emit('change', e.target);
              localValue = e.target.value;
              if (e.target.value.length > 0 && typeToOpen) {
                activate();
              }
            }
          "
        ></v-generic-input>
        <v-filter-generic-input
          v-else
          :model-value="localValue"
          :focus="focus"
          :name="name"
          :label="label"
          :placeholder="placeholder"
          @focus.stop="!typeToOpen ? activate() : () => undefined"
          @click.stop="!typeToOpen ? activate() : () => undefined"
          :debounce="500"
          autocomplete="off"
          :dynamic="dynamic"
          :small="true"
          :disabled="disabled"
          @input="
            (e) => {
              emit('change', e.target);
              localValue = e.target.value;
              if (e.target.value.length > 0 && typeToOpen) {
                activate();
              }
            }
          "
        ></v-filter-generic-input>
      </template>
      <template #default="{ side }">
        <div
          class="v-type-ahead-input__results"
          :class="`v-type-ahead-input--${side}`"
          :style="{ width: `${inputDomRect?.width || 300}px` }"
        >
          <div v-if="results?.length" class="result-items" @blur="deactivate">
            <div class="header">
              <slot name="result-header"> </slot>
            </div>
            <div
              v-for="(result, i) in results"
              :key="i"
              class="result"
              :class="{ selected: i === selectedIndex }"
              @click.stop.prevent="select(result)"
              @mouseover="change(i)"
            >
              <slot name="result" :row="result" :index="i"></slot>
            </div>
          </div>
          <div v-else class="not-found">
            Not found - click anywhere to use entered text
          </div>
        </div>
      </template>
    </v-popover>
  </div>
</template>

<style lang="scss">
.v-type-ahead-input {
  position: relative;
  width: 100%;
  &--bottom {
    margin-top: var(--spacing-xsmall);
  }
  &--top {
    margin-bottom: calc(var(--spacing-medium) * -1.25);
  }
  &__results {
    max-height: 300px;
    overflow-y: auto;
    padding: 12px 0;
    border-radius: var(--radius-xsmall);
    background-color: var(--color-bg-level-01);
    border: var(--border-stroke-default) solid
      var(--color-border-strong-emphasis);


    .header,
    .result {
      display: flex;
      padding: 0 var(--spacing-medium);
      font-size: var(--type-small-body);

      .label,
      .value {
        white-space: nowrap;
        min-width: 0;
        flex: 1 1 100%;
        overflow-x: hidden;
        text-overflow: ellipsis;
      }
    }

    .header {
      color: var(--color-text-subtle);
      .label {
        font-size: var(--type-small-body);
        padding-bottom: 12px;
      }
    }

    .result {
      font-size: var(--type-caption);
      padding: 12px var(--spacing-medium);
      cursor: pointer;
      &:hover {
        background-color: var(--color-bg-level-03);
      }

      &.selected {
        background-color: var(--color-bg-level-03);
      }
    }

    .not-found {
      margin: 12px var(--spacing-medium);
    }
  }

  &.dynamic {
    display: inline-block;
    width: auto;

    .results {
      width: 300px;
    }
  }

  &.disabled {
    pointer-events: none;
    .label,
    .message {
      color: var(--color-text-disabled);
    }
  }
}
</style>
